/**
 * SW2-2443 — the findings step of the offline project sync: each finding's
 * queued create/updates in order, then its queued photos.
 */
import db, { rekeyOfflineFinding } from '../db/db';
import findingApi from './api/finding';
import { syncPendingFindingPhotos } from './offlineFindingPhotos';
import { FAILED, hasPendingCreate, isPending, runStep, send } from './offlineSyncSend';

const sendPhotos = (finding, serverId) =>
  runStep(`photos for finding ${finding.id}`, () =>
    syncPendingFindingPhotos(finding, serverId),
  );

/**
 * Replay a finding's queue in order. Returns the finding's id afterwards — a
 * finding created offline moves to its server id once the create lands.
 *
 * Stops at the first entry that cannot go yet; later entries depend on it, so
 * they wait for the next sync:
 *
 *   - the server refused it;
 *   - it was made on an inspection created offline that does not exist on the
 *     server yet (a finding carried over into that inspection shares one queue
 *     with the inspection it came from, which syncs first);
 *   - its method is unknown, so there is nothing safe to send.
 */
async function syncFinding(finding, inspection, unsentInspectionIds) {
  let findingId = finding.id;

  let serverId = finding.sync.serverId ?? null;

  let queue = [...(finding.sync.payload || [])];

  while (queue.length) {
    const entry = queue[0];
    const body = { ...entry, images: [], completedImages: [] };
    const label = `finding ${findingId}`;

    if (unsentInspectionIds.has(Number(entry.inspectionId))) return findingId;

    if (entry.method === 'create') {
      // A server id already recorded means this create landed on an earlier run
      // whose local re-key did not finish: finish it rather than create again.
      if (!serverId) {
        const created = await send(label, () =>
          findingApi.create(inspection.id, body, { replay: true }),
        );

        if (created === FAILED || !created?.id) return findingId;

        serverId = created.id;
        await db.findings.update(findingId, { 'sync.serverId': serverId });
      }

      // Also drops this create from the queue.
      await rekeyOfflineFinding(inspection.project_id, findingId, serverId);
      findingId = serverId;
      queue = queue.slice(1);

      continue;
    }

    if (entry.method === 'update') {
      const targetId = serverId ?? findingId;
      const current = await db.findings.get(findingId);

      // Photos first — this update may close the finding, and the server
      // refuses a close with no closure photo (SW2-2136).
      await sendPhotos(current, targetId);

      const updated = await send(label, () =>
        findingApi.update(targetId, { ...body, id: targetId }, { replay: true }),
      );

      if (updated === FAILED) return findingId;
    } else {
      console.error(`Offline sync: ${label} has a queued "${entry.method}" it cannot send`);

      return findingId;
    }

    queue = queue.slice(1);
    await db.findings.update(findingId, { 'sync.payload': queue });
  }

  await db.findings.update(findingId, { 'sync.synced': true });

  return findingId;
}

export async function syncFindings(inspection) {
  const unsentInspectionIds = new Set(
    (
      await db.inspections
        .where('project_id')
        .equals(inspection.project_id)
        .toArray()
    )
      .filter(isPending)
      .map((i) => i.id),
  );
  const ids = (inspection.findings || []).map((f) => f.id);
  const findings = (await db.findings.bulkGet(ids)).filter(Boolean);

  for (const finding of findings) {
    const findingId = isPending(finding)
      ? await syncFinding(finding, inspection, unsentInspectionIds)
      : finding.id;

    // Anything still queued: photos on a finding with no update to replay,
    // added after the last one, or left behind by an earlier sync. A finding
    // whose create has not landed has no server id to attach them to.
    const current = await db.findings.get(findingId);

    if (current && !hasPendingCreate(current)) {
      await sendPhotos(current, current.sync?.serverId ?? current.id);
    }
  }
}
